import { useEffect, useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import type { AsyncAvData } from "@ui/hooks/useSessionAsyncAv";
import { useSessionAsyncAv } from "@ui/hooks/useSessionAsyncAv";
import { useTranslations } from "@ui/providers/useTranslations";
import { getBackendErrorCode } from "@ui/utils/backendError";
import querystring from "query-string";

import {
  isServerSide,
  removeAgeVerificationUuid,
  setAgeVerificationUuid,
  translateBackendErrorCode,
} from "@jti/fe-common";
import { useAvpActions } from "@jti/rest-api";

import { uuidCookieTtl, VERIFF_AV, YOTI_AV } from "../constants";
import type { AgeEstimationFlowConfig } from "../interfaces/AgeEstimationFlowConfig";
import type { AvFormValues } from "../interfaces/AvFormValues";
import type { AvUserDataType } from "../interfaces/AvUserDataType";
import { getAgeEstimationFallbackMethod, isAgeEstimationMethod } from "../utils/ageEstimationPolicy";
import {
  buildAsyncRedirectCallbackUrl,
  getAsyncProviderRedirectUrl,
  mapUserDataToAsyncAvData,
} from "../utils/asyncVerification";

import type { useAgeEstimationFallback } from "./useAgeEstimationFallback";
import type { AgeVerificationMethodType } from "./useAvMethods";
import { useAvVerificationRequests } from "./useAvVerificationRequests";
import { useWidgetAvDataCleanup } from "./useWidgetAvDataCleanup";

// Injected by the routing owner so ValidationMethods can run under React Router or Next App Router.
export type AgeVerificationNavigation = {
  search: string;
  replaceSearch: (search: string) => void;
};

interface UseAgeVerificationFlowProps {
  selectedMethod: string;
  selectMethod: (selectedMethod: string) => void;
  availableMethods: AgeVerificationMethodType[];
  allAvailableMethods: AgeVerificationMethodType[];
  form: UseFormReturn<AvFormValues>;
  userData: Partial<AvUserDataType>;
  isOptional?: boolean;
  baseUrl?: string;
  onSuccess?: (avpUuid?: string) => void;
  onFail?: (message?: string) => void;
  onLoadDataFromCache?: (asyncAvData: AsyncAvData) => void;
  onSaveDataToCache?: () => void;
  isAgeEstimationFlowEnabled: boolean;
  isAvailableMethodsLoading: boolean;
  ageEstimationFlow?: AgeEstimationFlowConfig;
  fallback: ReturnType<typeof useAgeEstimationFallback>;
  navigation: AgeVerificationNavigation;
}

/**
 * Encapsulates the full age verification orchestration: sync/async verify calls,
 * async provider redirect and script flows, the pending/error popup state, and
 * the recovery handlers wired to that popup. Behavior mirrors the original
 * ValidationMethods implementation.
 */
export function useAgeVerificationFlow({
  selectedMethod,
  selectMethod,
  availableMethods,
  allAvailableMethods,
  form,
  userData,
  isOptional,
  baseUrl,
  onSuccess,
  onFail,
  onLoadDataFromCache,
  onSaveDataToCache,
  isAgeEstimationFlowEnabled,
  isAvailableMethodsLoading,
  ageEstimationFlow,
  fallback,
  navigation,
}: UseAgeVerificationFlowProps) {
  const [isAsyncPendingPopupOpened, setAsyncPendingPopupOpened] = useState(false);
  const [hasAgeEstimationResponse, setHasAgeEstimationResponse] = useState(false);
  const [isValidationError, setIsValidationError] = useState(false);
  const [savedAsyncAvData, setSavedAsyncAvData] = useState<AsyncAvData | null>(null);

  const { dictionary } = useTranslations();
  const { search, replaceSearch } = navigation;
  const querySearch = useMemo(() => querystring.parse(search), [search]);
  const { getAsyncAvData, saveAsyncAvData, clearAsyncAvData } = useSessionAsyncAv();
  const { clearWidgetAvData } = useWidgetAvDataCleanup();
  const {
    verify,
    initAsyncVerification,
    asyncVerificationPending,
    asyncVerificationData,
    getAsyncMethodConfig,
    loadAsyncAgeVerificationScript,
    isVerifing,
    isAsyncVerificationFailed,
  } = useAvpActions();

  const onFallbackRequired = () => {
    removeAgeVerificationUuid();
    setAsyncPendingPopupOpened(false);
    setIsValidationError(false);
    fallback.openFallback();
  };

  const { asyncAgeVerify, verifyAge, handleCheckPrevAsyncVerification, onAgeVerificationFail, isFormFilled } =
    useAvVerificationRequests({
      verify,
      getAsyncMethodConfig,
      form,
      userData,
      selectedMethod,
      isOptional,
      clearWidgetAvData,
      onSuccess,
      onFail,
      setAsyncPendingPopupOpened,
      setIsValidationError,
      isAgeEstimationFlowEnabled,
      allAvailableMethods,
      onAgeEstimationResponse: () => setHasAgeEstimationResponse(true),
      onFallbackRequired,
    });

  useEffect(() => {
    if (asyncVerificationData) {
      const redirectUrl = getAsyncProviderRedirectUrl(asyncVerificationData);

      form.setValue("avpValues.request_uuid", asyncVerificationData.uuid ?? "");
      setAgeVerificationUuid(asyncVerificationData.uuid ?? "", uuidCookieTtl);

      if (redirectUrl) {
        onRedirectFlow(redirectUrl);
      } else {
        const workflowName = getAsyncMethodConfig(selectedMethod)?.workflowName;
        const workflowDefinitionId = getAsyncMethodConfig(selectedMethod)?.workflowDefinitionId;
        form.setValue("avpValues.workflowName", workflowName ?? "");
        form.setValue("avpValues.workflowDefinitionId", workflowDefinitionId ?? "");

        loadAsyncAgeVerificationScript(selectedMethod, asyncVerificationData, {
          token: asyncVerificationData.data?.token,
          onSuccess: () => {
            asyncAgeVerify({
              ageVerificationMethodCode: selectedMethod,
              ...mapUserDataToAsyncAvData(userData),
              request_uuid: asyncVerificationData.uuid ?? "",
              sessionUrl: "",
              workflowDefinitionId: workflowDefinitionId ?? "",
              workflowName: workflowName ?? "",
            });
          },
        });
      }
    }
  }, [asyncVerificationData]);

  useEffect(() => {
    // Age estimation classifies the return using the available methods, so wait
    // for them to load before processing the redirect back from the provider.
    if (isAgeEstimationFlowEnabled && isAvailableMethodsLoading) {
      return;
    }

    const asyncAvData = getAsyncAvData();
    if (querySearch.redirectedFromAsyncMethod && !Array.isArray(querySearch.redirectedFromAsyncMethod) && asyncAvData) {
      const redirectedMethod = querySearch.redirectedFromAsyncMethod;
      if (isAgeEstimationFlowEnabled && !isAgeEstimationMethod(redirectedMethod)) {
        fallback.setAuthorizedMethodCode(redirectedMethod);
      }
      selectMethod(redirectedMethod);
      setSavedAsyncAvData(asyncAvData);
      setHasAgeEstimationResponse(false);
      asyncAgeVerify(asyncAvData);
      setAsyncPendingPopupOpened(true);
      clearAsyncAvData();
      onLoadDataFromCache?.(asyncAvData);
      // Clear redirectedFromAsyncMethod from url after redirection, but preserve other params (e.g. returnUrl)
      const params = new URLSearchParams(search);
      params.delete("redirectedFromAsyncMethod");
      replaceSearch(params.toString());
    }
  }, [search, selectMethod, isAvailableMethodsLoading]);

  const onRedirectFlow = (redirectUrl: string) => {
    if (!asyncVerificationData) return;
    form.setValue("ageVerificationMethodCode", selectedMethod);

    saveAsyncAvData({
      ageVerificationMethodCode: selectedMethod,
      ...mapUserDataToAsyncAvData(userData),
      request_uuid: asyncVerificationData.uuid ?? "",
      sessionUrl: redirectUrl,
    });
    onSaveDataToCache?.();
    window.location.href = redirectUrl;
  };

  const initAsyncMethod = (selectedMethod: string) => {
    const callbackBaseUrl = baseUrl ?? (isServerSide() ? "" : window.location.href);

    return initAsyncVerification({
      methodCode: selectedMethod,
      userEmail: userData?.email || "",
      fields: {
        callback: buildAsyncRedirectCallbackUrl(callbackBaseUrl, selectedMethod),
        firstName: userData?.firstname || "",
        lastName: userData?.lastname || "",
        dateOfBirth: userData?.dateOfBirth,
        workflowName: getAsyncMethodConfig(selectedMethod)?.workflowName,
        workflowDefinitionId: getAsyncMethodConfig(selectedMethod)?.workflowDefinitionId,
      },
    });
  };

  const triggerVerification = async () => {
    const formValues = form.getValues();
    const verificationMethod = availableMethods.find(({ code }) => code === selectedMethod);
    if (isOptional && !isFormFilled() && verificationMethod?.type !== "async") {
      clearWidgetAvData();
      return onSuccess?.();
    }
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    const isVerificationSkipped = await handleCheckPrevAsyncVerification();

    if (isVerificationSkipped) {
      return;
    }

    if (verificationMethod?.type === "async") {
      try {
        await initAsyncMethod(selectedMethod);
      } catch (error) {
        onAgeVerificationFail(translateBackendErrorCode(getBackendErrorCode(error), undefined, dictionary));
      }
    } else {
      await verifyAge(formValues, selectedMethod);
    }
  };

  const tryAgainVerification = () => {
    if (savedAsyncAvData) {
      setIsValidationError(false);
      asyncAgeVerify(savedAsyncAvData);
    } else {
      setAsyncPendingPopupOpened(false);
    }
  };

  const returnToAsyncVerificationProcess = () => {
    if (selectedMethod === VERIFF_AV || selectedMethod === YOTI_AV) {
      setAsyncPendingPopupOpened(false);
      triggerVerification();
      return;
    }
    const verificationUrl = savedAsyncAvData?.sessionUrl;
    if (verificationUrl) {
      setIsValidationError(false);
      saveAsyncAvData(savedAsyncAvData);
      onSaveDataToCache?.();
      window.location.href = verificationUrl;
    } else {
      setAsyncPendingPopupOpened(false);
      setIsValidationError(false);
      triggerVerification();
    }
  };

  const cancelAsyncAgeVerificationProcess = () => {
    setAsyncPendingPopupOpened(false);
  };

  const continueWithFallback = async () => {
    const fallbackMethod = getAgeEstimationFallbackMethod(allAvailableMethods);
    if (!fallbackMethod) {
      return;
    }

    setAsyncPendingPopupOpened(false);
    fallback.setIsFallbackInitPending(true);
    fallback.setAuthorizedMethodCode(fallbackMethod.code);
    removeAgeVerificationUuid();
    clearAsyncAvData();
    selectMethod(fallbackMethod.code);

    try {
      await initAsyncMethod(fallbackMethod.code);
    } catch (error) {
      onAgeVerificationFail(translateBackendErrorCode(getBackendErrorCode(error), undefined, dictionary));
    } finally {
      fallback.setIsFallbackInitPending(false);
    }
  };

  const cancelFallback = () => {
    fallback.resetFallback();
    setIsValidationError(false);
    setAsyncPendingPopupOpened(false);
    removeAgeVerificationUuid();
    clearAsyncAvData();
    selectMethod("");
    ageEstimationFlow?.onCancelFallback();
  };

  return {
    isAsyncPendingPopupOpened,
    hasAgeEstimationResponse,
    isValidationError,
    isVerifing,
    asyncVerificationPending,
    isAsyncVerificationFailed,
    isFallbackOpened: fallback.isFallbackOpened,
    isFallbackInitPending: fallback.isFallbackInitPending,
    triggerVerification,
    tryAgainVerification,
    returnToAsyncVerificationProcess,
    cancelAsyncAgeVerificationProcess,
    continueWithFallback,
    cancelFallback,
  };
}
